import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Capability } from '@complan/domain';
import { usePageChrome } from '@/components/layout/page-chrome';
import { hasCapability, useSession } from '@/app/session/session';
import { cn } from '@/lib/utils';
import { MasterDataSection } from './MasterDataSection';
import { StatusFinalitySection } from './StatusFinalitySection';
import { ValidationRulesSection } from './ValidationRulesSection';
import { RolesSection } from './RolesSection';
import { OperationsSection } from './OperationsSection';

/**
 * The Administration screen (`/admin`), task 23.6.
 *
 * The screen presents the Master_Data_Administration surface as capability-gated
 * tabs (UI spec §3.1/§3.4/§3.5 component anatomy; the manpower-configuration card
 * of §5.1 is out of scope per §0.1). Each tab renders only when the session
 * principal holds the capability the corresponding routes require (R2.5):
 *
 * - `masterdata:admin` gates the master-data tables, the status/finality display
 *   editor and the configurable validation-rule table;
 * - `admin:roles` gates the role-assignment screen;
 * - `admin:ops` gates the operations backup/verification log.
 *
 * A principal without any of them sees the no-access notice, mirroring the server,
 * which denies every corresponding request regardless of what the client renders.
 */

interface TabDef {
  readonly id: string;
  readonly labelKey: string;
  readonly capability: Capability;
  readonly render: () => React.JSX.Element;
}

export function AdminPage(): React.JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  const me = session.data;

  usePageChrome({
    title: t('admin.page.title'),
    subtitle: t('admin.page.subtitle'),
  });

  const tabs = useMemo<readonly TabDef[]>(() => {
    const all: readonly TabDef[] = [
      {
        id: 'master-data',
        labelKey: 'admin.tab.masterData',
        capability: 'masterdata:admin',
        render: () => <MasterDataSection />,
      },
      {
        id: 'status-finality',
        labelKey: 'admin.tab.statusFinality',
        capability: 'masterdata:admin',
        render: () => <StatusFinalitySection />,
      },
      {
        id: 'validation-rules',
        labelKey: 'admin.tab.validationRules',
        capability: 'masterdata:admin',
        render: () => <ValidationRulesSection />,
      },
      {
        id: 'roles',
        labelKey: 'admin.tab.roles',
        capability: 'admin:roles',
        render: () => <RolesSection />,
      },
      {
        id: 'operations',
        labelKey: 'admin.tab.operations',
        capability: 'admin:ops',
        render: () => <OperationsSection />,
      },
    ];
    return all.filter((tab) => hasCapability(me, tab.capability));
  }, [me]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const currentId = activeId !== null && tabs.some((tab) => tab.id === activeId)
    ? activeId
    : tabs[0]?.id ?? null;
  const current = tabs.find((tab) => tab.id === currentId) ?? null;

  if (tabs.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-muted-foreground text-[12.5px]">{t('admin.noAccess')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div role="tablist" aria-label={t('admin.page.title')} className="border-line flex gap-1 border-b">
        {tabs.map((tab) => {
          const active = tab.id === currentId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveId(tab.id)}
              className={cn(
                'rounded-t-[6px] px-3 py-2 text-[12.5px] font-semibold transition-colors',
                active
                  ? 'border-x border-t border-[var(--line)] bg-panel text-[var(--accent-hover)]'
                  : 'text-muted-foreground hover:text-txt',
              )}
            >
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {current ? current.render() : null}
    </div>
  );
}
